'use client';

import { Link } from '@chakra-ui/next-js';
import { getSearchPath } from '@habity/services';
import { NestedSearch, TabButtonType } from '@habity/types';
import { SuggestionsButton } from '../Button';

// NOTE: This is copied from EO's agency settings
// We should eventually consolidate them to avoid the duplication
// I am also reusing the TabButtonType even though it doesn't really fit
// the new data structure anymore but I don't want to break
// existing functionaity right now by changing that
export const P0_SEARCH_TAG_OPTIONS = {
  // Village/small town properties
  Lantligt: {
    key: {
      categories: ['house', 'farm'],
      features: ['garden', 'rustic'],
      plot_size_min: 2500,
      build_year_max: 1950,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Sea villas
  Sjövillor: {
    key: {
      categories: ['house'],
      features: ['sea_view'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // City center properties
  Centralt: {
    key: {
      nearby: ['restaurant', 'cafe', 'bar'],
      features: ['commute_friendly'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Waterfront properties
  Havsnära: {
    key: {
      categories: ['house'],
      features: ['near_sea'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Energy-efficient homes
  Energihus: {
    key: {
      categories: ['house'],
      features: ['solar_panels', 'geothermal_heating'],
      build_year_min: 2000,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Senior living options
  Senior: {
    key: {
      categories: ['apartment'],
      nearby: ['pharmacy', 'supermarket'],
      features: ['elevator'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Student apartments near universities
  Student: {
    key: {
      nearby: ['university'],
      price_min: 1000000,
      price_max: 3000000,
      rooms_max: 2,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Properties in scenic natural settings
  Naturskönt: {
    key: {
      features: ['scenic_nature_area', 'mountain_view', 'garden'],
      plot_size_min: 2500,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Classic apartments with high ceilings
  Sekelskifte: {
    key: {
      features: ['high_ceiling', 'rustic'],
      build_year_max: 1940,
      price_min: 5000000,
      categories: ['apartment'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Family-friendly homes near schools
  Barnfamilj: {
    key: {
      categories: ['house'],
      nearby: ['kindergarten', 'school'],
      rooms_min: 3,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // New construction
  Nyproduktion: {
    key: {
      categories: ['new_construction'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Commercial
  Kommersiellt: {
    key: {
      categories: ['commercial'],
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Properties for horse enthusiasts
  Hästgård: {
    key: {
      categories: ['farm', 'house'],
      plot_size_min: 20000, // 1 hectare minimum
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Cozy homes
  Hygge: {
    key: {
      categories: ['house', 'cottage'],
      features: ['fireplace', 'rustic'],
      rooms_min: 2,
      rooms_max: 5,
      build_year_max: 1910,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Farms and rural properties
  Gård: {
    key: {
      categories: ['house', 'farm'],
      plot_size_min: 3500,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Properties with pools
  'Med pool': {
    key: {
      features: ['pool'],
      price_min: 4000000,
    },
  } as Omit<TabButtonType, 'title, icon'>,

  // Luxury apartments
  Lyxvåning: {
    key: {
      categories: ['apartment'],
      features: ['high_ceiling', 'reputed_area', 'rustic'],
      price_min: 8000000,
      rooms_min: 3,
      living_space_min: 120,
    },
  } as Omit<TabButtonType, 'title, icon'>,
};

interface P0SearchTagProps {
  tagType: keyof typeof P0_SEARCH_TAG_OPTIONS;
}

export function P0SearchTag({ tagType }: P0SearchTagProps) {
  const searchTag = P0_SEARCH_TAG_OPTIONS[tagType];

  return (
    <Link
      href={getSearchPath({
        filters: searchTag.key,
        tab: 'homes',
      })}
      whiteSpace="nowrap"
      _hover={{
        textDecoration: 'none',
      }}
    >
      <SuggestionsButton label={tagType} />
    </Link>
  );
}
